import { useState, useEffect, useRef } from 'react';

import { useRenewBondMutation } from 'redux/features/employeeBondApi';
import {
  useGetEmployeeWorkProfileQuery,
  useUpdateEmployeeWorkProfileMutation,
} from 'redux/features/users/userApi';
import { useGetDepartmentsQuery } from 'redux/features/departmentApi';
import { useGetDesignationsByDepartmentQuery } from 'redux/features/designationApi';

import { toast } from 'react-toastify';

import dayjs from 'lib/dayjs';

import Icon from 'components/AppIcon';

const emptyPeriod = () => ({
  id: null,
  period_type: '',
  start_date: '',
  end_date: '',
  remark: '',
  status: 'Active',
});

const RenewBondModal = ({ isOpen, onClose, bond }) => {
  const [renewBond, { isLoading: isRenewing }] = useRenewBondMutation();
  const { data: workProfile, isLoading: isLoadingWorkProfile } = useGetEmployeeWorkProfileQuery(
    bond?.user_id || 0,
    {
      skip: !bond || !bond.user_id,
    },
  );
  const [updateWorkProfile, { isLoading: isUpdatingProfile }] =
    useUpdateEmployeeWorkProfileMutation();

  const [formData, setFormData] = useState({
    new_start_date: '',
    new_end_date: '',
    signed_date: '',
    remarks: '',
    basic_salary: '',
    department_id: '',
    designation_id: '',
    current_experience_in_months: '',
  });

  const [periods, setPeriods] = useState([]);
  console.log({ periods });
  const [errors, setErrors] = useState({});
  const scrollContainerRef = useRef(null);

  const PERIOD_TYPE_OPTIONS = [
    { value: 'INTERNSHIP', label: 'Internship' },
    { value: 'PROBATION', label: 'Probation' },
    { value: 'TRAINEE', label: 'Training' },
    { value: 'EMPLOYMENT', label: 'Employment' },
    { value: 'NOTICE', label: 'Notice Period' },
  ];

  // Fetch departments
  const { data: departments, isLoading: isLoadingDepartments } = useGetDepartmentsQuery(undefined, {
    skip: false,
  });

  // Fetch designations by selected department
  const { data: designations, isLoading: isLoadingDesignations } =
    useGetDesignationsByDepartmentQuery(
      formData.department_id ? parseInt(formData.department_id) : undefined,
      {
        skip: !formData.department_id,
      },
    );

  useEffect(() => {
    if (isOpen && bond) {
      if (workProfile) {
        const deptId = workProfile.department?.id?.toString() || '';
        const desigId = workProfile.designation?.id?.toString() || '';

        setFormData({
          new_start_date: '',
          new_end_date: '',
          signed_date: '',
          remarks: '',
          basic_salary: workProfile.basic_salary || '',
          department_id: deptId,
          designation_id: desigId,
          current_experience_in_months: workProfile.current_experience_in_months?.toString() || '',
        });

        if (workProfile.employee_periods && workProfile.employee_periods.length > 0) {
          setPeriods(
            workProfile.employee_periods.map((p) => ({
              id: p.id || null,
              period_type: p.period_type || '',
              start_date: p.start_date || '',
              end_date: p.end_date || '',
              remark: p.remark || '',
              status: p.status || 'Active',
            })),
          );
        } else {
          setPeriods([]);
        }
      } else {
        setFormData({
          new_start_date: '',
          new_end_date: '',
          signed_date: '',
          remarks: '',
          basic_salary: '',
          department_id: '',
          designation_id: '',
          current_experience_in_months: '',
        });
        setPeriods([]);
      }
    }
  }, [isOpen, bond, workProfile]);

  // Auto-scroll to the first error field
  useEffect(() => {
    if (Object.keys(errors).length > 0 && scrollContainerRef.current) {
      const firstErrorEl = scrollContainerRef.current.querySelector('.border-red-400');
      if (firstErrorEl) {
        firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [errors]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [name]: '' }));
    if (name === 'department_id') {
      setErrors((prev) => ({ ...prev, designation_id: '' }));
      setFormData((prev) => ({ ...prev, [name]: value, designation_id: '' }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handlePeriodChange = (index, field, value) => {
    if (field === 'period_type') {
      setErrors((prev) => ({ ...prev, [`period_type_${index}`]: '' }));
    }
    if (field === 'start_date') {
      setErrors((prev) => ({ ...prev, [`period_start_${index}`]: '' }));
    }
    setPeriods((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleAddPeriod = () => {
    setPeriods((prev) => [...prev, emptyPeriod()]);
  };

  const handleRemovePeriod = (index) => {
    setPeriods((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.new_start_date.trim()) newErrors.new_start_date = 'Start date is required';
    if (!formData.new_end_date.trim()) newErrors.new_end_date = 'End date is required';
    if (formData.new_start_date && formData.new_end_date) {
      if (dayjs(formData.new_end_date).isBefore(dayjs(formData.new_start_date))) {
        newErrors.new_end_date = 'End date must be after start date';
      }
    }

    if (formData.new_start_date && bond?.end_date) {
      if (!dayjs(formData.new_start_date).isAfter(dayjs(bond.end_date))) {
        newErrors.new_start_date = 'New start date must be after current bond end date';
      }
    }

    if (!formData.basic_salary?.toString().trim()) {
      newErrors.basic_salary = 'Basic salary is required';
    } else if (isNaN(Number(formData.basic_salary)) || Number(formData.basic_salary) <= 0) {
      newErrors.basic_salary = 'Basic salary must be a positive number';
    }

    if (!formData.department_id) {
      newErrors.department_id = 'Department is required';
    }

    if (!formData.designation_id) {
      newErrors.designation_id = 'Designation is required';
    }

    if (!formData.current_experience_in_months?.toString().trim()) {
      newErrors.current_experience_in_months = 'Experience is required';
    } else if (
      isNaN(Number(formData.current_experience_in_months)) ||
      Number(formData.current_experience_in_months) < 0
    ) {
      newErrors.current_experience_in_months = 'Experience must be a valid number';
    }

    periods.forEach((period, index) => {
      if (!period.period_type.trim()) {
        newErrors[`period_type_${index}`] = 'Period type is required';
      }
      if (!period.start_date) {
        newErrors[`period_start_${index}`] = 'Start date is required';
      }
    });

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const employeePeriods = periods.map((p) => {
        const base = {
          period_type: p.period_type,
          start_date: p.start_date,
          end_date: p.end_date || null,
          remark: p.remark,
          status: p.status,
        };
        if (p.id) base.id = p.id;
        return base;
      });

      await updateWorkProfile({
        id: bond.user_id,
        department_id: formData.department_id ? parseInt(formData.department_id) : null,
        designation_id: formData.designation_id ? parseInt(formData.designation_id) : null,
        current_experience_in_months: formData.current_experience_in_months
          ? parseInt(formData.current_experience_in_months)
          : null,
        salary: {
          basic_salary: formData.basic_salary,
        },
        employee_periods: employeePeriods,
      }).unwrap();

      // 2. Renew bond
      await renewBond({
        id: bond.id,
        new_start_date: formData.new_start_date,
        new_end_date: formData.new_end_date,
        signed_date: formData.signed_date || undefined,
        remarks: formData.remarks || undefined,
      }).unwrap();

      toast.success('Bond renewed & profile updated successfully');
      onClose();
    } catch (error) {
      toast.error(error?.data?.message || 'Something went wrong');
    }
  };

  if (!isOpen || !bond) return null;

  const userName = bond.user ? `${bond.user.first_name} ${bond.user.last_name}` : 'N/A';
  const deptName = bond.user?.department?.name || 'N/A';
  const isSubmitting = isRenewing || isUpdatingProfile;

  return (
    <div className="fixed inset-0   z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 transition-opacity" onClick={onClose} />

      {/* Modal wrapper */}
      <div className=" flex min-h-full   mt-20 items-center justify-center pt-4 sm:p-6 max-h-[calc(100vh-300px)]">
        <div
          className="relative w-full    max-w-4xl bg-white rounded-2xl shadow-xl border border-gray-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white rounded-t-2xl">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center">
                <Icon name="RefreshCw" size={18} className="text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Renew Employee Bond</h3>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <Icon name="X" size={18} />
            </button>
          </div>

          {/* Scrollable body */}
          <div
            ref={scrollContainerRef}
            className="px-6 py-5 max-h-[calc(100vh-180px)] overflow-y-auto"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Employee Info */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                    <span className="text-sm font-bold text-primary-700">
                      {bond.user?.first_name?.[0]}
                      {bond.user?.last_name?.[0]}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{userName}</p>
                    <p className="text-xs text-gray-500">
                      {bond.user?.employee_id} · {deptName}
                    </p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs font-medium text-gray-500 mb-0.5">Current Bond Period</p>
                  <p className="text-sm text-gray-700">
                    {dayjs(bond.start_date).format('DD MMM YYYY')} →{' '}
                    {bond.end_date ? dayjs(bond.end_date).format('DD MMM YYYY') : 'No end date'}
                  </p>
                </div>
              </div>
              {/* Bond Renewal Fields */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 space-y-4">
                <h4 className="text-base font-semibold text-gray-900">New Bond Period</h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* New Start Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="new_start_date"
                      value={formData.new_start_date}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm ${
                        errors.new_start_date ? 'border-red-400 bg-red-50' : 'border-gray-300'
                      }`}
                    />
                    {errors.new_start_date && (
                      <p className="text-red-500 text-xs mt-1">{errors.new_start_date}</p>
                    )}
                  </div>

                  {/* New End Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New End Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="new_end_date"
                      value={formData.new_end_date}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm ${
                        errors.new_end_date ? 'border-red-400 bg-red-50' : 'border-gray-300'
                      }`}
                    />
                    {errors.new_end_date && (
                      <p className="text-red-500 text-xs mt-1">{errors.new_end_date}</p>
                    )}
                  </div>

                  {/* Signed Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Signed Date <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="date"
                      name="signed_date"
                      value={formData.signed_date}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                {/* Remarks */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Remarks <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <textarea
                    name="remarks"
                    value={formData.remarks}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
                    placeholder="Any additional notes..."
                  />
                </div>
              </div>

              {/* Work Profile Section */}
              {workProfile && !isLoadingWorkProfile && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 space-y-4">
                  <h4 className="text-base font-semibold text-gray-900">Work Profile</h4>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Basic Salary */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Basic Salary <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="basic_salary"
                        value={formData.basic_salary}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm ${
                          errors.basic_salary ? 'border-red-400 bg-red-50' : 'border-gray-300'
                        }`}
                      />
                      {errors.basic_salary && (
                        <p className="text-red-500 text-xs mt-1">{errors.basic_salary}</p>
                      )}
                    </div>

                    {/* Department */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Department <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="department_id"
                        value={formData.department_id}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm ${
                          errors.department_id ? 'border-red-400 bg-red-50' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select Department</option>
                        {isLoadingDepartments ? (
                          <option disabled>Loading departments...</option>
                        ) : (
                          departments?.departments?.map((dept) => (
                            <option key={dept.id} value={String(dept.id)}>
                              {dept.name}
                            </option>
                          ))
                        )}
                      </select>
                      {errors.department_id && (
                        <p className="text-red-500 text-xs mt-1">{errors.department_id}</p>
                      )}
                    </div>

                    {/* Designation */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Designation <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="designation_id"
                        value={formData.designation_id}
                        onChange={handleChange}
                        disabled={!formData.department_id}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed ${
                          errors.designation_id ? 'border-red-400 bg-red-50' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select Designation</option>
                        {isLoadingDesignations ? (
                          <option disabled>Loading designations...</option>
                        ) : (
                          designations?.map((d) => (
                            <option key={d.id} value={String(d.id)}>
                              {d.name}
                            </option>
                          ))
                        )}
                      </select>
                      {errors.designation_id && (
                        <p className="text-red-500 text-xs mt-1">{errors.designation_id}</p>
                      )}
                    </div>

                    {/* Experience */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Experience (months) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="current_experience_in_months"
                        value={formData.current_experience_in_months}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm ${
                          errors.current_experience_in_months
                            ? 'border-red-400 bg-red-50'
                            : 'border-gray-300'
                        }`}
                      />
                      {errors.current_experience_in_months && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.current_experience_in_months}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Employee Periods - editable list */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-base font-semibold text-gray-900">Employee Periods</h4>
                      <button
                        type="button"
                        onClick={handleAddPeriod}
                        className="px-3 py-1.5 bg-primary hover:bg-primary-600 text-white rounded-lg text-xs font-medium transition-colors flex items-center gap-1"
                      >
                        <Icon name="Plus" size={14} />
                        Add Period
                      </button>
                    </div>

                    {periods.length > 0 ? (
                      <div className="space-y-3">
                        {periods.map((period, index) => (
                          <div
                            key={index}
                            className="bg-white rounded-lg p-3 border border-gray-200 space-y-3"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-gray-500">
                                {period.id ? 'Existing' : 'New'} Period
                              </span>
                              <button
                                type="button"
                                onClick={() => handleRemovePeriod(index)}
                                className="w-6 h-6 flex items-center justify-center rounded text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                title="Remove period"
                              >
                                <Icon name="Trash2" size={14} />
                              </button>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {/* Period Type */}
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Period Type <span className="text-red-500">*</span>
                                </label>
                                <select
                                  type="text"
                                  value={period.period_type}
                                  onChange={(e) =>
                                    handlePeriodChange(index, 'period_type', e.target.value)
                                  }
                                  placeholder="e.g. Bond, Training"
                                  className={`w-full px-3 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm ${
                                    errors[`period_type_${index}`]
                                      ? 'border-red-400 bg-red-50'
                                      : 'border-gray-300'
                                  }`}
                                >
                                  <option value="">Select Period Type</option>
                                  {PERIOD_TYPE_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                                {errors[`period_type_${index}`] && (
                                  <p className="text-red-500 text-xs mt-0.5">
                                    {errors[`period_type_${index}`]}
                                  </p>
                                )}
                              </div>

                              {/* Status */}
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Status
                                </label>
                                <select
                                  value={period.status}
                                  onChange={(e) =>
                                    handlePeriodChange(index, 'status', e.target.value)
                                  }
                                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                                >
                                  <option value="Active">Active</option>
                                  <option value="Completed">Completed</option>
                                  <option value="Terminated">Terminated</option>
                                </select>
                              </div>

                              {/* Start Date */}
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Start Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="date"
                                  value={period.start_date}
                                  onChange={(e) =>
                                    handlePeriodChange(index, 'start_date', e.target.value)
                                  }
                                  className={`w-full px-3 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm ${
                                    errors[`period_start_${index}`]
                                      ? 'border-red-400 bg-red-50'
                                      : 'border-gray-300'
                                  }`}
                                />
                                {errors[`period_start_${index}`] && (
                                  <p className="text-red-500 text-xs mt-0.5">
                                    {errors[`period_start_${index}`]}
                                  </p>
                                )}
                              </div>

                              {/* End Date */}
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  End Date
                                </label>
                                <input
                                  type="date"
                                  value={period.end_date}
                                  onChange={(e) =>
                                    handlePeriodChange(index, 'end_date', e.target.value)
                                  }
                                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                                />
                              </div>

                              {/* Remark */}
                              <div className="sm:col-span-2">
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Remark
                                </label>
                                <input
                                  type="text"
                                  value={period.remark}
                                  onChange={(e) =>
                                    handlePeriodChange(index, 'remark', e.target.value)
                                  }
                                  placeholder="Optional note..."
                                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm text-center py-6 italic">
                        No employee periods found
                      </p>
                    )}
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 z-10 flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-white rounded-b-2xl">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-5 py-2 bg-primary hover:bg-primary-600 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Icon name="Loader" size={14} className="animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Icon name="CheckCircle" size={14} />
                  Renew Bond
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RenewBondModal;
